import { Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { environment } from 'src/environments/environment';
import { oAuthConfig } from '../auth/auth.config';

/**
 * Configuração OIDC do CyberArk Identity (Portal Minha Área).
 *
 * O issuer (ica) inclui o path do app no subdomínio do tenant CyberArk, por
 * isso `skipIssuerCheck` e `strictDiscoveryDocumentValidation: false` são
 * obrigatórios — caso contrário o discovery document não passa na validação
 * estrita. Usa Authorization Code Flow + PKCE (responseType: 'code').
 */
export const authMinhaAreaConfig: AuthConfig = {
  issuer: environment.ica,
  clientId: environment.cca,
  redirectUri: window.location.origin + '/cyberark-callback',
  responseType: 'code',
  scope: 'openid profile email',
  strictDiscoveryDocumentValidation: false,
  skipIssuerCheck: true,
  requireHttps: environment.production,
  showDebugInformation: !environment.production,
  customQueryParams: {
    login_hint: 'user@idpsp',
    submitUsername: 'true',
  },
};

/**
 * Fluxo de login via CyberArk / Portal Minha Área.
 *
 * Convive com o login gov.br direto já existente (AuthService) sem alterá-lo.
 * A ideia central: o login no CyberArk já estabelece a sessão federada no
 * gov.br. Por isso, após o callback do CyberArk, disparamos o login gov.br
 * com `prompt=none` para reaproveitar a sessão e convergir, de forma
 * silenciosa, no mesmo callback /loginsuccess (AuthCallbackComponent) que o
 * fluxo gov.br direto já utiliza.
 */
@Injectable({ providedIn: 'root' })
export class LoginCyberArkService {
  constructor(private readonly oauthService: OAuthService) {}

  /** Redireciona o navegador para o Portal Minha Área (redirect puro, sem OIDC aqui). */
  cyberArkLogin(): void {
    window.location.href = environment.cyberArkMinhaArea;
  }

  /** Configura o OAuthService para o OIDC do CyberArk (usado no /cyberark-callback). */
  configurarCyberArk(): void {
    this.oauthService.configure(authMinhaAreaConfig);
  }

  /**
   * Após autenticar no CyberArk, dispara o login gov.br reaproveitando a
   * sessão federada (prompt=none). Reconfigura o OAuthService com a mesma
   * config do gov.br direto, apenas trocando prompt=login -> prompt=none, e
   * mantém o redirectUri /loginsuccess para convergir no AuthCallbackComponent.
   *
   * Importante: o configure() limpa o cache do discovery document; por isso
   * recarregamos o discovery antes do initCodeFlow.
   */
  govBrLoginFederado(): void {
    const federatedConfig: AuthConfig = {
      ...oAuthConfig,
      customQueryParams: {
        ...((oAuthConfig.customQueryParams as Record<string, string>) ?? {}),
        prompt: 'none',
      },
    };
    this.oauthService.configure(federatedConfig);
    this.oauthService
      .loadDiscoveryDocument()
      .then(() => this.oauthService.initCodeFlow())
      .catch((error) => console.error('Erro ao carregar discovery gov.br (federado):', error));
  }
}
